/**
 * AdminEventForm - Create / edit a pop-up event and manage its participants
 * Participants management is delegated to EventParticipantsPanel
 * @atomic @idempotent
 */
import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { eventsAPI } from '../../services/api/events'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'
import { ArrowLeftIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { labelClasses, getInputClasses, getSelectClasses, errorMessageClasses } from '../../styles/inputStyles'
import RichTextEditor from './RichTextEditor'
import EventParticipantsPanel from './EventParticipantsPanel'
import { useGeocoding } from '../../hooks/useGeocoding'

const logger = createLogger('AdminEventForm')
const AdminEventForm = memo(function AdminEventForm({ event, onSave, onCancel }) {
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()
  const { geocode, reverseGeocode, isGeocoding } = useGeocoding()

  const isEditing = !!event?.id

  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    imageUrl: event?.imageUrl || '',
    locationName: event?.locationName || '',
    address: event?.address || '',
    latitude: event?.latitude ?? '',
    longitude: event?.longitude ?? '',
    startAt: event?.startAt ? new Date(event.startAt).toISOString().slice(0, 16) : '',
    endAt: event?.endAt ? new Date(event.endAt).toISOString().slice(0, 16) : '',
    isRecurring: event?.isRecurring || false,
    recurrencePattern: event?.recurrencePattern || '',
    recurrenceDayOfWeek: event?.recurrenceDayOfWeek ?? '',
    recurrenceEndDate: event?.recurrenceEndDate || '',
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [eventId, setEventId] = useState(event?.id || null)

  const validate = () => {
    const errs = {}
    if (!form.title.trim()) errs.title = t('admin.events.validation.titleRequired')
    if (!form.startAt) errs.startAt = t('admin.events.validation.startRequired')
    if (!form.endAt) errs.endAt = t('admin.events.validation.endRequired')
    if (form.startAt && form.endAt && new Date(form.endAt) <= new Date(form.startAt)) {
      errs.endAt = t('admin.events.validation.endAfterStart')
    }
    return errs
  }

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    setErrors(prev => ({ ...prev, [name]: undefined }))
  }, [])

  const handleSave = useCallback(async () => {
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setSaving(true)

    const payload = {
      title: form.title.trim(),
      description: form.description || null,
      imageUrl: form.imageUrl || null,
      locationName: form.locationName || null,
      address: form.address || null,
      latitude: form.latitude !== '' ? parseFloat(form.latitude) : null,
      longitude: form.longitude !== '' ? parseFloat(form.longitude) : null,
      startAt: new Date(form.startAt).toISOString(),
      endAt: new Date(form.endAt).toISOString(),
      isRecurring: form.isRecurring,
      recurrencePattern: form.isRecurring ? form.recurrencePattern || null : null,
      recurrenceDayOfWeek: form.isRecurring && form.recurrenceDayOfWeek !== '' ? parseInt(form.recurrenceDayOfWeek) : null,
      recurrenceEndDate: form.isRecurring ? form.recurrenceEndDate || null : null,
    }

    await execute(
      () => isEditing ? eventsAPI.adminUpdateEvent(event.id, payload) : eventsAPI.adminCreateEvent(payload),
      {
        onSuccess: (res) => {
          if (!isEditing) setEventId(res.data?.id)
          toast.success(isEditing ? t('admin.events.updated') : t('admin.events.created'))
          if (isEditing) onSave()
        },
        onError: (err) => {
          logger.error('Failed to save event', err)
          toast.error(err.message)
        }
      }
    )
    setSaving(false)
  }, [form, isEditing, event, execute, onSave, t]) // eslint-disable-line react-hooks/exhaustive-deps

  const DAYS = [
    t('common.days.sunday'), t('common.days.monday'), t('common.days.tuesday'),
    t('common.days.wednesday'), t('common.days.thursday'), t('common.days.friday'),
    t('common.days.saturday')
  ]

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Back */}
      <button
        onClick={onCancel}
        onTouchEnd={onCancel}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t('common.back')}
      </button>

      <h2 className="text-2xl font-bold text-gray-900">
        {isEditing ? t('admin.events.editTitle') : t('admin.events.createTitle')}
      </h2>

      {/* ── Event Details ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
        <h3 className="font-semibold text-gray-800">{t('admin.events.section.details')}</h3>

        <div>
          <label className={labelClasses}>{t('admin.events.field.title')} *</label>
          <input name="title" value={form.title} onChange={handleChange}
            className={getInputClasses(!!errors.title)} placeholder={t('admin.events.field.titlePlaceholder')} />
          {errors.title && <p className={errorMessageClasses}>{errors.title}</p>}
        </div>

        <div>
          <label className={labelClasses}>{t('admin.events.field.description')}</label>
          <RichTextEditor
            value={form.description}
            onChange={(html) => setForm(prev => ({ ...prev, description: html }))}
            placeholder={t('admin.events.field.descriptionPlaceholder')}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClasses}>{t('admin.events.field.startAt')} *</label>
            <input name="startAt" type="datetime-local" value={form.startAt} onChange={handleChange}
              className={getInputClasses(!!errors.startAt)} />
            {errors.startAt && <p className={errorMessageClasses}>{errors.startAt}</p>}
          </div>
          <div>
            <label className={labelClasses}>{t('admin.events.field.endAt')} *</label>
            <input name="endAt" type="datetime-local" value={form.endAt} onChange={handleChange}
              className={getInputClasses(!!errors.endAt)} />
            {errors.endAt && <p className={errorMessageClasses}>{errors.endAt}</p>}
          </div>
        </div>

        <div>
          <label className={labelClasses}>{t('admin.events.field.locationName')}</label>
          <input name="locationName" value={form.locationName} onChange={handleChange}
            className={getInputClasses(false)} placeholder={t('admin.events.field.locationNamePlaceholder')} />
        </div>

        <div>
          <label className={labelClasses}>{t('admin.events.field.address')}</label>
          <div className="flex gap-2">
            <input name="address" value={form.address} onChange={handleChange}
              className={`${getInputClasses(false)} flex-1`} />
            <button
              type="button"
              disabled={isGeocoding || !form.address.trim()}
              onClick={async () => {
                const result = await geocode(form.address)
                if (result) {
                  setForm(prev => ({ ...prev, latitude: result.latitude, longitude: result.longitude }))
                } else {
                  toast.error('Address not found')
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <MapPinIcon className="h-3.5 w-3.5" />
              {isGeocoding ? 'Looking up...' : 'Get coords'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClasses}>{t('admin.events.field.latitude')}</label>
            <input name="latitude" type="number" step="any" value={form.latitude} onChange={handleChange}
              className={getInputClasses(false)} />
          </div>
          <div>
            <label className={labelClasses}>{t('admin.events.field.longitude')}</label>
            <div className="flex gap-2">
              <input name="longitude" type="number" step="any" value={form.longitude} onChange={handleChange}
                className={`${getInputClasses(false)} flex-1`} />
              <button
                type="button"
                disabled={isGeocoding || form.latitude === '' || form.longitude === ''}
                onClick={async () => {
                  const result = await reverseGeocode(parseFloat(form.latitude), parseFloat(form.longitude))
                  if (result) {
                    setForm(prev => ({ ...prev, address: result.formattedAddress || result.fullAddress || prev.address }))
                  } else {
                    toast.error('Reverse geocode failed')
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <MapPinIcon className="h-3.5 w-3.5" />
                {isGeocoding ? '...' : 'Fill addr'}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className={labelClasses}>{t('admin.events.field.imageUrl')}</label>
          <input name="imageUrl" value={form.imageUrl} onChange={handleChange}
            className={getInputClasses(false)} placeholder="https://..." />
        </div>
      </div>

      {/* ── Recurrence ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{t('admin.events.section.recurrence')}</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" name="isRecurring" checked={form.isRecurring} onChange={handleChange}
              className="h-4 w-4 text-primary rounded" />
            <span className="text-sm text-gray-600">{t('admin.events.field.isRecurring')}</span>
          </label>
        </div>

        {form.isRecurring && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses}>{t('admin.events.field.recurrencePattern')}</label>
              <select name="recurrencePattern" value={form.recurrencePattern} onChange={handleChange}
                className={getSelectClasses()}>
                <option value="">{t('common.select')}</option>
                <option value="weekly">{t('admin.events.recurrence.weekly')}</option>
                <option value="biweekly">{t('admin.events.recurrence.biweekly')}</option>
                <option value="monthly">{t('admin.events.recurrence.monthly')}</option>
              </select>
            </div>
            <div>
              <label className={labelClasses}>{t('admin.events.field.recurrenceDayOfWeek')}</label>
              <select name="recurrenceDayOfWeek" value={form.recurrenceDayOfWeek} onChange={handleChange}
                className={getSelectClasses()}>
                <option value="">{t('common.select')}</option>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelClasses}>{t('admin.events.field.recurrenceEndDate')}</label>
              <input name="recurrenceEndDate" type="date" value={form.recurrenceEndDate} onChange={handleChange}
                className={getInputClasses(false)} />
            </div>
          </div>
        )}
      </div>

      {/* ── Save button ── */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          onTouchEnd={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? t('common.saving') : isEditing ? t('common.save') : t('admin.events.saveAndAddParticipants')}
        </button>
        <button onClick={onCancel} onTouchEnd={onCancel}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          {t('common.cancel')}
        </button>
      </div>

      {/* ── Participants (only after save gives us an eventId) ── */}
      {eventId && (
        <EventParticipantsPanel
          eventId={eventId}
          initialParticipants={event?.participants || []}
          onDone={onSave}
        />
      )}
    </div>
  )
})

export default AdminEventForm
